// src/components/MarketInsights/components/SummaryCards.tsx

import React from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Apartment as ApartmentIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  SquareFoot as SqftIcon,
  LocalOffer as SpecialsIcon,
} from "@mui/icons-material";
import { SummaryStats } from "../types";
import { formatCurrency, formatNumber, formatPercent, formatPricePerSqft } from "../utils/formatters";

import { overviewNavigationUrl } from "../../../lib/propertiesOverviewSlice";

interface SummaryCardsProps {
  onNavigate?: (page:string)=>void;
  data: SummaryStats | undefined;
  isLoading: boolean;
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  isLoading?: boolean;
  compact?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  isLoading,
  compact = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: compact ? 1.5 : undefined, "&:last-child": compact ? { pb: 1.5 } : undefined }}>
          <Skeleton width="60%" height={20} />
          <Skeleton width="80%" height={24} sx={{ mt: 0.5 }} />
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ py: 1.25, px: 2, "&:last-child": { pb: 1.25 } }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ color, display: "flex", flexShrink: 0, pt: 0.25, "& svg": { fontSize: 20 } }}>
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, flexShrink: 0 }}>
                  {value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }} noWrap>
                  {title}
                </Typography>
              </Box>
              {subtitle && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(color, 0.1),
              color: color,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mt: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SummaryCards: React.FC<SummaryCardsProps> = ({ data, isLoading, onNavigate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const cards = [
    {title:'Active buildings',value:formatNumber(data?.total_properties),filter:'active',icon:<ApartmentIcon/>,color:theme.palette.primary.main},
    {title:'Available now',value:formatNumber(data?.available_now),filter:'available',icon:<CheckIcon/>,color:theme.palette.success.main},
    {title:'With current specials',value:formatNumber(data?.specials_count),filter:'specials',icon:<SpecialsIcon/>,color:theme.palette.warning.main},
    {title:'Median rent',value:formatCurrency(data?.median_rent),filter:'median',icon:<MoneyIcon/>,color:theme.palette.info.main},
  ];
  function openOverview(filter:string) {
    window.history.replaceState(window.history.state,'',overviewNavigationUrl(window.location.href,filter,data?.median_rent));
    onNavigate?.('properties');
  }

  return (
    <Box><Typography variant="caption" display="block" sx={{mb:1}}>{!data ? (isLoading ? "Loading inventory summary…" : "No inventory summary data yet.") : <>Across {formatNumber(data.total_properties)} active buildings and {formatNumber(data.total_units)} units · oldest recorded capture {data.data_as_of ? new Date(data.data_as_of).toLocaleDateString() : "unknown"} · {data.freshness_unknown_count ?? "unknown"} buildings with unknown freshness. Rent uses units with a recorded price.</>}</Typography><Grid container spacing={isMobile ? 1 : 2}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <ButtonBase disabled={isLoading || !data || !onNavigate} onClick={()=>openOverview(card.filter)} sx={{width:"100%",display:"block",textAlign:"left"}}><StatCard {...card} isLoading={isLoading} compact={isMobile} /></ButtonBase>
        </Grid>
      ))}
    </Grid></Box>
  );
};

export default SummaryCards;
